/**
 * Minimal jQuery shim to ease migration from jQuery to native JavaScript
 * This provides basic jQuery-like API using native DOM methods
 *
 * Usage: import $ from '../utils/jqueryShim';
 */

// Helper to convert NodeList to Array
const toArray = (nodeList) => Array.from(nodeList || []);

// Window wrapper class for $(window) calls
class WindowWrapper {
  scroll(handler) {
    window.addEventListener('scroll', handler);
    return this;
  }

  resize(handler) {
    window.addEventListener('resize', handler);
    return this;
  }

  scrollTop() {
    return window.pageYOffset;
  }

  innerHeight() {
    return window.innerHeight;
  }

  width() {
    return window.innerWidth;
  }

  height() {
    return window.innerHeight;
  }

  on(event, handler) {
    window.addEventListener(event, handler);
    return this;
  }

  off(event, handler) {
    window.removeEventListener(event, handler);
    return this;
  }

  unbind(event) {
    // Can't easily unbind all listeners on window
    return this;
  }
}

// Singleton for window wrapper
const windowWrapper = new WindowWrapper();

// Main wrapper function
function $(selector) {
  // Handle already wrapped elements
  if (selector instanceof DOMWrapper) {
    return selector;
  }

  // Handle window object
  if (selector === window) {
    return windowWrapper;
  }

  // Handle DOM elements
  if (selector instanceof Element || selector instanceof HTMLDocument) {
    return new DOMWrapper([selector]);
  }

  // Handle selector strings
  if (typeof selector === 'string') {
    // Check if it's HTML or a selector
    if (selector.trim().startsWith('<')) {
      const temp = document.createElement('div');
      temp.innerHTML = selector.trim();
      return new DOMWrapper(toArray(temp.children));
    }
    return new DOMWrapper(toArray(document.querySelectorAll(selector)));
  }

  // Handle functions (document ready)
  if (typeof selector === 'function') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', selector);
    } else {
      selector();
    }
    return new DOMWrapper([]);
  }

  return new DOMWrapper([]);
}

// jQuery-like wrapper class
class DOMWrapper {
  constructor(elements) {
    this.elements = elements || [];
    this.length = this.elements.length;
    // Make it array-like
    this.elements.forEach((el, i) => {
      this[i] = el;
    });
  }

  // Iteration
  each(callback) {
    this.elements.forEach(callback);
    return this;
  }

  // DOM traversal
  find(selector) {
    const results = [];
    this.elements.forEach(el => {
      results.push(...toArray(el.querySelectorAll(selector)));
    });
    return new DOMWrapper(results);
  }

  closest(selector) {
    if (this.elements[0]) {
      const result = this.elements[0].closest(selector);
      return new DOMWrapper(result ? [result] : []);
    }
    return new DOMWrapper([]);
  }

  parent() {
    if (this.elements[0] && this.elements[0].parentElement) {
      return new DOMWrapper([this.elements[0].parentElement]);
    }
    return new DOMWrapper([]);
  }

  prev() {
    if (this.elements[0] && this.elements[0].previousElementSibling) {
      return new DOMWrapper([this.elements[0].previousElementSibling]);
    }
    return new DOMWrapper([]);
  }

  // Class manipulation
  addClass(className) {
    this.elements.forEach(el => el.classList.add(className));
    return this;
  }

  removeClass(className) {
    this.elements.forEach(el => el.classList.remove(className));
    return this;
  }

  toggleClass(className) {
    this.elements.forEach(el => el.classList.toggle(className));
    return this;
  }

  hasClass(className) {
    return this.elements[0] ? this.elements[0].classList.contains(className) : false;
  }

  // Attributes
  attr(name, value) {
    if (value === undefined) {
      return this.elements[0] ? this.elements[0].getAttribute(name) : null;
    }
    this.elements.forEach(el => el.setAttribute(name, value));
    return this;
  }

  removeAttr(name) {
    this.elements.forEach(el => el.removeAttribute(name));
    return this;
  }

  // CSS
  css(prop, value) {
    if (typeof prop === 'object') {
      this.elements.forEach(el => {
        Object.keys(prop).forEach(key => {
          el.style[key] = prop[key];
        });
      });
      return this;
    }
    if (value === undefined) {
      return this.elements[0] ? window.getComputedStyle(this.elements[0])[prop] : null;
    }
    this.elements.forEach(el => {
      el.style[prop] = value;
    });
    return this;
  }

  // Content
  text(value) {
    if (value === undefined) {
      return this.elements[0] ? this.elements[0].textContent : '';
    }
    this.elements.forEach(el => {
      el.textContent = value;
    });
    return this;
  }

  html(value) {
    if (value === undefined) {
      return this.elements[0] ? this.elements[0].innerHTML : '';
    }
    this.elements.forEach(el => {
      el.innerHTML = value;
    });
    return this;
  }

  val(value) {
    if (value === undefined) {
      return this.elements[0] ? this.elements[0].value : '';
    }
    this.elements.forEach(el => {
      el.value = value;
    });
    return this;
  }

  // Visibility
  show() {
    this.elements.forEach(el => {
      el.style.display = '';
    });
    return this;
  }

  hide() {
    this.elements.forEach(el => {
      el.style.display = 'none';
    });
    return this;
  }

  fadeIn(duration) {
    // Simple show without animation (jQuery fadeIn would animate)
    return this.show();
  }

  fadeOut(duration) {
    // Simple hide without animation
    return this.hide();
  }

  // Events
  on(event, selector, handler) {
    // Simple event delegation
    if (typeof selector === 'function') {
      handler = selector;
      selector = null;
    }

    this.elements.forEach(el => {
      if (selector) {
        el.addEventListener(event, (e) => {
          if (e.target.matches(selector)) {
            handler.call(e.target, e);
          }
        });
      } else {
        el.addEventListener(event, handler);
      }
    });
    return this;
  }

  off(event, handler) {
    this.elements.forEach(el => {
      el.removeEventListener(event, handler);
    });
    return this;
  }

  click(handler) {
    if (handler) {
      return this.on('click', handler);
    }
    this.elements.forEach(el => el.click());
    return this;
  }

  unbind(event) {
    // Note: This won't work perfectly since we don't track listeners
    // Better to use .off() with specific handlers
    this.elements.forEach(el => {
      const clone = el.cloneNode(true);
      el.parentNode.replaceChild(clone, el);
    });
    return new DOMWrapper(this.elements.map(el => {
      return el.parentNode.querySelector(`#${el.id}`) || el;
    }));
  }

  // Data storage
  data(key, value) {
    if (value === undefined) {
      // Get data
      if (!this.elements[0]) return null;
      const el = this.elements[0];
      if (!el._dataStore) el._dataStore = {};
      return el._dataStore[key];
    }
    // Set data
    this.elements.forEach(el => {
      if (!el._dataStore) el._dataStore = {};
      el._dataStore[key] = value;
    });
    return this;
  }

  // Dimensions
  width() {
    return this.elements[0] ? this.elements[0].offsetWidth : 0;
  }

  height() {
    return this.elements[0] ? this.elements[0].offsetHeight : 0;
  }

  outerHeight() {
    return this.elements[0] ? this.elements[0].offsetHeight : 0;
  }

  // Position
  offset() {
    if (!this.elements[0]) return { top: 0, left: 0 };
    const rect = this.elements[0].getBoundingClientRect();
    return {
      top: rect.top + window.pageYOffset,
      left: rect.left + window.pageXOffset,
    };
  }

  // Filtering
  not(selector) {
    let filtered;
    if (typeof selector === 'string') {
      filtered = this.elements.filter(el => !el.matches(selector));
    } else if (selector instanceof DOMWrapper) {
      filtered = this.elements.filter(el => !selector.elements.includes(el));
    } else if (selector instanceof Element) {
      filtered = this.elements.filter(el => el !== selector);
    } else {
      filtered = this.elements;
    }
    return new DOMWrapper(filtered);
  }

  // Bootstrap popover compatibility stub
  popover(action) {
    // This is a stub - actual popover requires Bootstrap
    console.warn('popover() requires Bootstrap.js - using stub');
    return this;
  }
}

// jQuery.fn prototype - needed for plugins like Highcharts
$.fn = DOMWrapper.prototype;
$.fn.jquery = '3.7.1-shim'; // Fake version for compatibility checks

// Static methods
$.extend = function(...args) {
  return Object.assign(...args);
};

$.inArray = function(value, array) {
  return array.indexOf(value);
};

$.each = function(obj, callback) {
  if (Array.isArray(obj)) {
    obj.forEach((val, i) => callback(i, val));
  } else {
    Object.keys(obj).forEach(key => callback(key, obj[key]));
  }
};

$.data = function(element, key, value) {
  if (!element._dataStore) element._dataStore = {};
  if (value === undefined) {
    return element._dataStore[key];
  }
  element._dataStore[key] = value;
  return value;
};

$.isFunction = function(obj) {
  return typeof obj === 'function';
};

$.isPlainObject = function(obj) {
  return obj !== null && typeof obj === 'object' && obj.constructor === Object;
};

$.isArray = Array.isArray;

$.noop = function() {};

$.trim = function(str) {
  return str ? str.trim() : '';
};

// For compatibility with libraries that check $.ajax
$.ajax = function() {
  console.warn('$.ajax is not supported - use fetch() instead');
  return Promise.reject(new Error('$.ajax not supported'));
};

export default $;
